import { BUTTER, CORAL, LILAC, MINT, PAPER, PEACH, SKY, lerpColor } from '../../color';
import type { World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, rgba, spots, starPath, vignette, type Painter, type Spot } from '../../art/paint';

const TAU = Math.PI * 2;
const NEBULAE: readonly (readonly number[])[] = [
  [SKY, LILAC, SKY],
  [MINT, SKY, MINT],
  [CORAL, LILAC, PEACH],
];
const PLANETS = [LILAC, MINT, PEACH];

function gradient(p: Painter, look: Look): void {
  const ctx = p.ctx;
  const bg = look.palette.bg;
  const g = ctx.createLinearGradient(0, 0, p.w * 0.3, p.h);
  g.addColorStop(0, rgba(lerpColor(bg, 0x000000, 0.2)));
  g.addColorStop(0.6, rgba(bg));
  g.addColorStop(1, rgba(lerpColor(bg, LILAC, 0.16)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
}

function blob(p: Painter, x: number, y: number, r: number, color: number, alpha: number): void {
  const ctx = p.ctx;
  for (let layer = 0; layer < 3; layer++) {
    const rr = r * (1 - layer * 0.22);
    const phase = p.rng.float() * TAU;
    ctx.beginPath();
    for (let i = 0; i <= 28; i++) {
      const a = (i / 28) * TAU;
      const k = 1 + 0.16 * Math.sin(3 * a + phase) + 0.08 * Math.sin(5 * a + phase * 2);
      const px = x + Math.cos(a) * rr * k;
      const py = y + Math.sin(a) * rr * k * 0.75;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = rgba(color, alpha);
    ctx.fill();
    ctx.strokeStyle = rgba(color, alpha * 0.9);
    ctx.lineWidth = 2;
    ctx.stroke();
  }
}

function nebula(p: Painter): void {
  const tones = NEBULAE[(p.level - 1) % NEBULAE.length] ?? NEBULAE[0] ?? [SKY];
  for (const at of spots(p, 7, 0.75, -40, p.unit * 0.35)) {
    const r = p.unit * p.rng.range(0.28, 0.55);
    const tone = p.rng.pick(tones);
    glowDot(p.ctx, at.x, at.y, r * 1.6, tone, 0.1);
    blob(p, at.x, at.y, r, tone, 0.05);
  }
}

function swirls(p: Painter): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(PAPER, SKY, 0.4), 0.14);
  ctx.lineWidth = 1.4;
  ctx.lineCap = 'round';
  for (const at of spots(p, 6, 1.0, 20, p.unit * 0.3)) {
    const s = p.unit * p.rng.range(0.08, 0.14);
    const dir = p.rng.chance(0.5) ? 1 : -1;
    for (let ring = 0; ring < 2; ring++) {
      ctx.beginPath();
      for (let i = 0; i <= 50; i++) {
        const t = i / 50;
        const a = dir * t * TAU * 1.6 + ring;
        const r = s * (0.2 + t) * (1 + ring * 0.25);
        const px = at.x + Math.cos(a) * r * 1.4;
        const py = at.y + Math.sin(a) * r * 0.8;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }
  }
}

function stars(p: Painter): void {
  const ctx = p.ctx;
  for (let i = 0; i < 150; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * p.h;
    if (calmAt(p, x, y, 0.8)) continue;
    ctx.fillStyle = rgba(p.rng.chance(0.2) ? BUTTER : PAPER, p.rng.range(0.2, 0.55));
    ctx.beginPath();
    ctx.arc(x, y, p.rng.range(0.5, 1.4), 0, TAU);
    ctx.fill();
  }
  for (const at of spots(p, 9, 0.95, 20, 80)) {
    const r = p.rng.range(5, 10);
    glowDot(ctx, at.x, at.y, r * 3, BUTTER, 0.18);
    ctx.fillStyle = rgba(lerpColor(PAPER, BUTTER, 0.4), 0.7);
    starPath(ctx, at.x, at.y, r, 0.22, 4, 0);
    ctx.fill();
  }
}

function constellation(p: Painter, at: Spot): void {
  const ctx = p.ctx;
  const pts: Spot[] = [at];
  for (let i = 0; i < 4; i++) {
    const last = pts[pts.length - 1] ?? at;
    const a = p.rng.float() * TAU;
    const d = p.unit * p.rng.range(0.07, 0.13);
    const next = { x: last.x + Math.cos(a) * d, y: last.y + Math.sin(a) * d };
    if (calmAt(p, next.x, next.y, 0.95)) break;
    pts.push(next);
  }
  ctx.strokeStyle = rgba(lerpColor(PAPER, SKY, 0.4), 0.28);
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 5]);
  ctx.beginPath();
  pts.forEach((pt, i) => {
    if (i === 0) ctx.moveTo(pt.x, pt.y);
    else ctx.lineTo(pt.x, pt.y);
  });
  ctx.stroke();
  ctx.setLineDash([]);
  for (const pt of pts) {
    ctx.fillStyle = rgba(PAPER, 0.7);
    starPath(ctx, pt.x, pt.y, 4, 0.3, 4, 0.3);
    ctx.fill();
  }
}

function planet(p: Painter, at: Spot, look: Look): void {
  const ctx = p.ctx;
  const r = Math.max(28, p.unit * p.rng.range(0.11, 0.15));
  const body = lerpColor(look.palette.bg, PLANETS[(p.level - 1) % PLANETS.length] ?? LILAC, 0.45);
  const tilt = p.rng.range(-0.5, -0.2);
  const ring = lerpColor(look.palette.bg, PAPER, 0.35);
  ctx.strokeStyle = rgba(ring, 0.55);
  ctx.lineWidth = Math.max(3, r * 0.1);
  ctx.beginPath();
  ctx.ellipse(at.x, at.y, r * 1.8, r * 0.45, tilt, Math.PI, TAU);
  ctx.stroke();
  glowDot(ctx, at.x, at.y, r * 2.2, body, 0.18);
  ctx.fillStyle = rgba(body);
  ctx.beginPath();
  ctx.arc(at.x, at.y, r, 0, TAU);
  ctx.fill();
  ctx.save();
  ctx.beginPath();
  ctx.arc(at.x, at.y, r, 0, TAU);
  ctx.clip();
  ctx.strokeStyle = rgba(lerpColor(body, PAPER, 0.25), 0.5);
  ctx.lineWidth = r * 0.12;
  for (const k of [-0.45, 0.05, 0.5]) {
    ctx.beginPath();
    ctx.ellipse(at.x, at.y + k * r, r * 1.2, r * 0.18, tilt * 0.4, 0, TAU);
    ctx.stroke();
  }
  ctx.fillStyle = rgba(lerpColor(body, 0x000000, 0.45), 0.55);
  ctx.beginPath();
  ctx.arc(at.x, at.y, r * 1.02, 0, TAU);
  ctx.arc(at.x - r * 0.28, at.y - r * 0.28, r * 0.95, 0, TAU, true);
  ctx.fill('evenodd');
  ctx.restore();
  ctx.strokeStyle = rgba(ring, 0.65);
  ctx.lineWidth = Math.max(3, r * 0.1);
  ctx.beginPath();
  ctx.ellipse(at.x, at.y, r * 1.8, r * 0.45, tilt, 0, Math.PI);
  ctx.stroke();
}

function crescent(p: Painter, at: Spot): void {
  const ctx = p.ctx;
  const r = Math.max(18, p.unit * 0.07);
  glowDot(ctx, at.x, at.y, r * 3.2, BUTTER, 0.14);
  ctx.fillStyle = rgba(lerpColor(PAPER, BUTTER, 0.35), 0.75);
  ctx.beginPath();
  ctx.arc(at.x, at.y, r, 0, TAU);
  ctx.arc(at.x + r * 0.45, at.y - r * 0.2, r * 0.85, 0, TAU, true);
  ctx.fill('evenodd');
}

function comet(p: Painter, at: Spot): void {
  const ctx = p.ctx;
  const len = p.unit * 0.35;
  const a = p.rng.range(0.25, 0.6) + (at.x > p.cx ? Math.PI : 0);
  const tx = at.x - Math.cos(a) * len;
  const ty = at.y - Math.sin(a) * len;
  const g = ctx.createLinearGradient(at.x, at.y, tx, ty);
  g.addColorStop(0, rgba(PAPER, 0.45));
  g.addColorStop(1, rgba(SKY, 0));
  ctx.strokeStyle = g;
  ctx.lineCap = 'round';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(at.x, at.y);
  ctx.lineTo(tx, ty);
  ctx.stroke();
  glowDot(ctx, at.x, at.y, 16, PAPER, 0.35);
}

function clouds(p: Painter, look: Look): void {
  const ctx = p.ctx;
  for (let layer = 0; layer < 2; layer++) {
    const base = p.h - p.h * (0.06 + 0.07 * (1 - layer));
    const tone = lerpColor(look.palette.bg, LILAC, layer === 0 ? 0.16 : 0.24);
    const r = p.unit * (0.12 + layer * 0.03);
    ctx.fillStyle = rgba(tone);
    ctx.beginPath();
    ctx.moveTo(-20, p.h + 10);
    for (let x = -r; x < p.w + r; x += r * p.rng.range(0.9, 1.3)) {
      const y = base + p.rng.range(-r * 0.3, r * 0.3) + layer * r * 0.4;
      ctx.arc(x, y, r * p.rng.range(0.7, 1.05), Math.PI, TAU);
    }
    ctx.lineTo(p.w + 20, p.h + 10);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = rgba(lerpColor(tone, PAPER, 0.25), 0.5);
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}

export function paint(p: Painter, look: Look): void {
  gradient(p, look);
  nebula(p);
  swirls(p);
  stars(p);
  const places = spots(p, 6, 1.05, 50, p.unit * 0.4);
  const planetAt = places.shift();
  if (planetAt !== undefined) planet(p, planetAt, look);
  const moonAt = places.shift();
  if (moonAt !== undefined) crescent(p, moonAt);
  const cometAt = places.shift();
  if (cometAt !== undefined) comet(p, cometAt);
  for (const at of places) constellation(p, at);
  clouds(p, look);
  calmCenter(p, look.palette.bg, 0.55, 1.15);
  grain(p, 0.04, true);
  vignette(p, 0x000000, 0.4);
}
